import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ShieldCheck, Award, CheckCircle2, XCircle, Network, Search, Loader2,
  Calendar, Building2, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';

export default function VerifyCertificatePage() {
  const { certificateId } = useParams();
  const [searchId, setSearchId] = useState(certificateId || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (certificateId) {
      verifyCertificate(certificateId);
    }
  }, [certificateId]);

  const verifyCertificate = async (id) => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const res = await api.get(`/verify/${id}`);
      setResult(res.data);
    } catch (err) {
      setError('Failed to verify certificate. Please check the ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchId.trim()) {
      verifyCertificate(searchId.trim());
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      A: 'text-emerald-400 bg-emerald-400/20 border-emerald-500',
      B: 'text-blue-400 bg-blue-400/20 border-blue-500',
      C: 'text-amber-400 bg-amber-400/20 border-amber-500',
      D: 'text-orange-400 bg-orange-400/20 border-orange-500',
      F: 'text-red-400 bg-red-400/20 border-red-500'
    };
    return colors[grade] || colors.C;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="verify-certificate-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/corpcraft-logo.png" 
              alt="CorpCraft Logo" 
              className="h-10 w-auto object-contain"
            />
          </Link>
          <Link to="/" className="text-slate-400 hover:text-white text-sm flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-4xl font-bold font-['Outfit'] mb-2">Certificate Verification</h1>
          <p className="text-slate-400">Verify the authenticity of a CorpCraft certification</p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-8">
          <Input
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            placeholder="Enter Certificate ID (e.g., CC-XXXXXXXX)"
            className="flex-1 bg-slate-900 border-slate-800 h-12"
            data-testid="certificate-id-input"
          />
          <Button
            type="submit"
            disabled={loading || !searchId.trim()}
            className="gradient-accent hover:opacity-90 h-12 px-6"
            data-testid="verify-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Verify
              </>
            )}
          </Button>
        </form>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-6 text-center mb-8" data-testid="verify-error">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6" data-testid="verify-result">
            {result.valid ? (
              <>
                {/* Valid Certificate Card */}
                <div className="stat-card text-center border-emerald-500/30">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-emerald-400 mb-2">Valid Certificate</h2>
                  <p className="text-slate-400">This certificate is authentic and verified</p>
                </div>

                {/* Certificate Details */}
                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-800">
                    <Award className="w-6 h-6 text-amber-400" />
                    <span className="font-semibold">Certificate Details</span>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Certificate ID</p>
                      <code className="text-sm bg-slate-800 px-3 py-1 rounded">{result.certificate_id}</code>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
                      <span className="text-emerald-400 font-medium">{result.status}</span>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Grade</p>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getGradeColor(result.grade)}`}>
                        {result.grade}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Score</p>
                      <span className="text-2xl font-bold">{result.score}</span>
                      <span className="text-slate-500">/100</span>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Entities Audited</p>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span>{result.entity_count} entities</span>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Issued Date</p>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{formatDate(result.issued_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="stat-card text-center">
                  <h3 className="font-semibold mb-4">Share This Certificate</h3>
                  <div className="inline-block bg-white p-4 rounded-xl mb-4">
                    <QRCodeSVG 
                      value={`${window.location.origin}/verify/${result.certificate_id}`}
                      size={150}
                      level="H"
                    />
                  </div>
                  <p className="text-sm text-slate-400">Scan to verify this certificate</p>
                </div>
              </>
            ) : (
              /* Invalid Certificate */
              <div className="stat-card text-center border-red-500/30">
                <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">Invalid Certificate</h2>
                <p className="text-slate-400">This certificate ID was not found in our records</p>
                <p className="text-sm text-slate-500 mt-4">
                  Please check the certificate ID and try again. If you believe this is an error, 
                  please contact the certificate issuer.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        {!result && !error && !loading && (
          <div className="stat-card text-center">
            <Award className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">What is CorpCraft Certification?</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              CorpCraft Certified structures have been audited by our Sentinel Scoring Engine 
              and meet our standards for corporate structure best practices, including proper 
              entity separation, jurisdiction optimization, and asset protection.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
